import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PATH_PROJECTS, PATH_STORES, PATH_CONTRACTORS } from "@/constants";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { ExternalLink } from "lucide-react";
import { openNewWindow } from "@/lib/utils";
import { useContractorCardData } from "../hooks";
import { CardRequireBadge } from "@/modules/contractor-panel/cards/shared/components";

export const ContractorCardDetails = () => {
  const { t } = useTranslation();
  const contractor = useContractorCardData();
  const { formatDateTime } = useFormatters();

  const handleOpenProject = useCallback(() => {
    openNewWindow(`${PATH_PROJECTS}/${contractor.project_id}`);
  }, [contractor.project_id]);

  const handleOpenStore = useCallback(() => {
    openNewWindow(`${PATH_STORES}/${contractor.store_id}`);
  }, [contractor.store_id]);

  const handleOpenContractor = useCallback(() => {
    openNewWindow(`${PATH_CONTRACTORS}/${contractor.contractor_id}`);
  }, [contractor.contractor_id]);

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
          {t("contractorCards.fields.project_name")}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={handleOpenProject}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </dt>
        <dd className="text-sm text-gray-900">{contractor.project_name}</dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
          {t("contractorCards.fields.store_name")}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={handleOpenStore}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </dt>
        <dd className="text-sm text-gray-900">{contractor.store_name}</dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
          {t("contractorCards.fields.contractor_name")}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={handleOpenContractor}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </dt>
        <dd className="text-sm text-gray-900">{contractor.contractor_name}</dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.period")}
        </dt>
        <dd className="text-sm text-gray-900">{contractor.period}</dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.contractor_card_required")}
        </dt>
        <dd className="text-sm text-gray-900">
          <CardRequireBadge required={contractor.contractor_card_required} />
        </dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.card_filename")}
        </dt>
        <dd className="text-sm text-gray-900">
          {contractor.card_filename || "-"}
        </dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.card_mimetype")}
        </dt>
        <dd className="text-sm text-gray-900">
          {contractor.card_mimetype ? (
            <Badge variant="outline">{contractor.card_mimetype}</Badge>
          ) : (
            "-"
          )}
        </dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.created_at")}
        </dt>
        <dd className="text-sm text-gray-900">
          {contractor.created_at ? formatDateTime(contractor.created_at) : "-"}
        </dd>
      </div>

      <div className="space-y-1">
        <dt className="text-sm font-medium text-gray-500">
          {t("contractorCards.fields.updated_at")}
        </dt>
        <dd className="text-sm text-gray-900">
          {contractor.updated_at ? formatDateTime(contractor.updated_at) : "-"}
        </dd>
      </div>
    </dl>
  );
};
